import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { ExternalServiceProvidersService } from '../../../services/external-service-providers.service';
import { ExternalServiceProvider } from '../../../models/ExternalServiceProvider';

export interface ProviderSelection {
  id: number;
  tradeName: string;
  companyName: string;
  cnpj: string;
  phone: string;
  contactEmail: string;
  responsibleName: string;
  serviceType: string;
  servedStates: string;
}

@Component({
  selector: 'app-provider-selection-modal',
  templateUrl: './provider-selection-modal.component.html',
  styleUrls: ['./provider-selection-modal.component.css']
})
export class ProviderSelectionModalComponent implements OnInit {
  @Input() serviceTypeFilter: string | null = null;
  @Input() modalTitle = 'Selecionar Prestador de Serviço';
  @Output() providerSelected = new EventEmitter<ProviderSelection>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  searchTerm: string = '';
  providers: ProviderSelection[] = [];
  filteredProviders: ProviderSelection[] = [];
  selectedProvider: ProviderSelection | null = null;
  isLoading: boolean = false;

  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;

  constructor(private externalServiceProvidersService: ExternalServiceProvidersService) {}

  ngOnInit(): void {}

  get Math() {
    return Math;
  }

  loadProviders(): void {
    this.isLoading = true;

    this.externalServiceProvidersService.list(
      this.currentPage,
      this.pageSize,
      this.searchTerm || undefined,
      undefined,
      this.serviceTypeFilter || undefined
    ).subscribe({
      next: (response) => {
        this.totalCount = response.pagination?.totalCount ?? 0;
        this.totalPages = response.pagination?.totalPages ?? 0;
        this.currentPage = response.pagination?.pageNumber ?? this.currentPage;
        this.providers = this.mapToSelection(response.items);
        this.filteredProviders = [...this.providers];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar prestadores de serviço:', error);
        this.isLoading = false;
      }
    });
  }

  private mapToSelection(items: ExternalServiceProvider[]): ProviderSelection[] {
    return items.map(p => ({
      id: p.id,
      tradeName: p.tradeName || '',
      companyName: p.companyName || '',
      cnpj: p.cnpj || '',
      phone: p.phone || 'Não informado',
      contactEmail: p.contactEmail || 'Não informado',
      responsibleName: p.responsibleName || 'Não informado',
      serviceType: p.serviceType || 'Não informado',
      servedStates: p.servedStates || 'Não informado'
    }));
  }

  open(options?: { serviceTypeFilter?: string | null; title?: string }): void {
    this.serviceTypeFilter = options?.serviceTypeFilter ?? null;
    this.modalTitle = options?.title || (this.serviceTypeFilter
      ? 'Selecionar fornecedor de guincho'
      : 'Selecionar Prestador de Serviço');
    this.showModal = true;
    this.searchTerm = '';
    this.selectedProvider = null;
    this.currentPage = 1;
    this.loadProviders();
  }

  close(): void {
    this.showModal = false;
    this.closeModal.emit();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadProviders();
  }

  selectProvider(provider: ProviderSelection): void {
    this.selectedProvider = provider;
  }

  confirmSelection(): void {
    if (this.selectedProvider) {
      this.providerSelected.emit(this.selectedProvider);
      this.close();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.selectedProvider = null;
      this.loadProviders();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.selectedProvider = null;
      this.loadProviders();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.selectedProvider = null;
      this.loadProviders();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfWindow = Math.floor(maxPagesToShow / 2);
      let startPage = Math.max(1, this.currentPage - halfWindow);
      let endPage = Math.min(this.totalPages, this.currentPage + halfWindow);

      if (this.currentPage <= halfWindow) {
        endPage = maxPagesToShow;
      }
      if (this.currentPage >= this.totalPages - halfWindow) {
        startPage = this.totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  formatCnpj(cnpj: string): string {
    if (!cnpj) return '';
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length !== 14) return cnpj;
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
}
